import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { productAPI } from '../services/api';

const AGE_GROUPS = [
  { key: '1-3', label: '1-3 years', adjustment: 0 },
  { key: '4-7', label: '4-7 years', adjustment: 500 },
  { key: '7-10', label: '7-10 years', adjustment: 1000 }
];

const getCategoryPricingProfile = (product) => {
  const productCategories = product?.categories?.length ? product.categories : (product?.category ? [product.category] : []);
  const ageCategory = productCategories.find((category) => category?.pricingType === 'age');
  const sizeCategory = productCategories.find((category) => category?.pricingType === 'size');
  const category = ageCategory || sizeCategory || productCategories[0];
  const categoryName = (category?.name || '').toLowerCase();
  const pricingType = category?.pricingType;
  const categoryGroup = (category?.categoryGroup || '').toLowerCase();

  if (pricingType) return { type: pricingType === 'size' ? 'women-size' : pricingType };
  if (categoryGroup === 'girl' || categoryGroup === 'boy/men') return { type: 'age' };
  if (categoryGroup === 'women') return { type: 'women-size' };
  if (categoryName.includes('girl') || categoryName.includes('boy')) return { type: 'age' };
  if (categoryName.includes('women')) return { type: 'women-size' };
  return { type: 'base' };
};

const getAgeAdjustedPrice = (basePrice, ageGroup) => {
  const selected = AGE_GROUPS.find((group) => group.key === ageGroup) || AGE_GROUPS[0];
  return Number(basePrice || 0) + Number(selected.adjustment || 0);
};

const getSizeAdjustedPrice = (basePrice, size) => {
  if (!size) return Number(basePrice || 0);
  const normalized = String(size).trim().toLowerCase();
  return ['2xl', '3xl'].includes(normalized)
    ? Number(basePrice || 0) + 1000
    : Number(basePrice || 0);
};

const getConfiguredPrice = (product, pricingProfile, ageGroup, size) => {
  const agePrice = pricingProfile.type === 'age'
    ? getAgeAdjustedPrice(product.price, ageGroup)
    : Number(product.price || 0);
  const sizeAdjustment = ['age', 'women-size'].includes(pricingProfile.type)
    ? getSizeAdjustedPrice(0, size)
    : 0;
  return agePrice + sizeAdjustment;
};

const normalizeImageUrl = (url) => {
  if (!url) return '';
  const value = String(url).trim();
  if (!value) return '';

  const safeValue = value
    .replace(/\.heic(?=[?#]|$)/gi, '.jpg')
    .replace(/\.heif(?=[?#]|$)/gi, '.jpg');

  if (safeValue.includes('cloudinary.com') && !/[?&]fm=jpg/i.test(safeValue)) {
    return `${safeValue}${safeValue.includes('?') ? '&' : '?'}fm=jpg`;
  }

  return safeValue;
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('1-3');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productAPI.getProductById(id);
        const productData = response.data.product;
        setProduct(productData);
        setSelectedImage(
          normalizeImageUrl(productData.images?.find((img) => img.isPrimary)?.url || productData.images?.[0]?.url || '')
        );
        setSelectedSize(productData.sizes?.[0] || '');
      } catch (error) {
        console.error('Error fetching product details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const formatPrice = (value) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(value || 0);

  const isUnlimitedStock = Boolean(product?.isUnlimitedStock);

  const updateQuantity = (step) => {
    if (isUnlimitedStock) return;

    const maxAllowed = Number(product?.stock || 1);
    setQuantity((prev) => Math.min(maxAllowed, Math.max(1, prev + step)));
  };

  const handleAddToCart = async () => {
    const pricingProfile = getCategoryPricingProfile(product);
    const finalPrice = getConfiguredPrice(product, pricingProfile, selectedAgeGroup, selectedSize);

    await addToCart({
      ...product,
      size: selectedSize,
      color: product.colors?.[0]?.name || 'Default',
      cartImage: selectedImage || product.images?.[0]?.url || '',
      quantity,
      ...(pricingProfile.type === 'age' ? { ageGroup: selectedAgeGroup } : {}),
      price: finalPrice
    });
    navigate('/cart');
  };

  const handleBuyNow = async () => {
    const pricingProfile = getCategoryPricingProfile(product);
    const finalPrice = getConfiguredPrice(product, pricingProfile, selectedAgeGroup, selectedSize);

    await addToCart({
      ...product,
      size: selectedSize,
      color: product.colors?.[0]?.name || 'Default',
      cartImage: selectedImage || product.images?.[0]?.url || '',
      quantity,
      ...(pricingProfile.type === 'age' ? { ageGroup: selectedAgeGroup } : {}),
      price: finalPrice
    });
    navigate('/cart');
  };

  const askOnWhatsApp = () => {
    const productLink = `${window.location.origin}/product/${product._id}`;
    const message = `Hi, I am interested in ${product.name}.\nProduct link: ${productLink}\nQuantity: ${quantity}${selectedSize ? `\nSize: ${selectedSize}` : ''}${selectedAgeGroup ? `\nAge group: ${selectedAgeGroup}` : ''}`;
    window.open(`https://wa.me/918309158686?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="page-loading">Loading product...</div>;
  }

  if (!product) {
    return (
      <div className="product-detail empty-state-block">
        <h2>Product not found</h2>
        <button className="primary-btn" onClick={() => navigate('/')}>
          Back to Home
        </button>
      </div>
    );
  }

  const images = product.images || [];
  const videos = product.videos || [];
  const pricingProfile = getCategoryPricingProfile(product);
  const currentPrice = getConfiguredPrice(product, pricingProfile, selectedAgeGroup, selectedSize);

  return (
    <div className="product-detail-page">
      <div className="product-gallery">
        <div className="main-image-wrap">
          <img src={normalizeImageUrl(selectedImage || images[0]?.url)} alt={product.name} className="main-product-image" />
        </div>
        {videos.map((video, index) => (
          <div className="product-video-wrap" key={`${video.url}-${index}`}>
            <video
              src={video.url}
              className="product-detail-video"
              controls
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label={`${product.name} video ${index + 1}`}
            />
          </div>
        ))}
        <div className="thumb-row">
          {images.map((image, index) => (
            <button
              key={`${image.url}-${index}`}
              className={`thumb ${selectedImage === image.url ? 'active' : ''}`}
              type="button"
              onClick={() => setSelectedImage(image.url)}
            >
              <img src={normalizeImageUrl(image.url)} alt={`${product.name} ${index + 1}`} />
            </button>
          ))}
        </div>
      </div>

      <div className="product-info">
        <h1>{product.name}</h1>
        <p className="product-description detail-text">{product.description}</p>

        <div className="price-block">
          <span className="detail-price">{formatPrice(currentPrice)}</span>
          {product.originalPrice > product.price && (
            <span className="detail-original">{formatPrice(product.originalPrice)}</span>
          )}
        </div>

        {pricingProfile.type === 'age' && (
          <div className="size-section">
            <div className="selector-header">
              <span>Select Age Group</span>
            </div>
            <div className="size-options">
              {AGE_GROUPS.map((group) => (
                <button
                  key={group.key}
                  type="button"
                  className={`size-btn ${selectedAgeGroup === group.key ? 'selected' : ''}`}
                  onClick={() => setSelectedAgeGroup(group.key)}
                >
                  {group.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="size-section">
          <div className="selector-header">
            <span>{pricingProfile.type === 'age' ? 'Select Size' : pricingProfile.type === 'women-size' ? 'Select Size' : 'Select Size'}</span>
            <button type="button" className="text-link">Help me choose a size</button>
          </div>
          <div className="size-options">
            {product.sizes?.map((size) => (
              <button
                key={size}
                type="button"
                className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                onClick={() => setSelectedSize(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {pricingProfile.type === 'women-size' && (
          <p className="checkout-note">Women’s pricing: S–XL = base price. 2XL–3XL = +₹1000.</p>
        )}

        <p className="combo-measurement-notice">We will contact you via WhatsApp to collect your measurements for a perfect custom fit.</p>

        <div className="order-note">
          <p>Production estimate: approx. 14-21 days (2-3 weeks) after your order</p>
        </div>

        <div className="quantity-row">
          <span>Quantity</span>
          <div className="quantity-box">
            {isUnlimitedStock ? (
              <span>Unlimited</span>
            ) : (
              <>
                <button type="button" onClick={() => updateQuantity(-1)} disabled={quantity <= 1}>-</button>
                <span>{quantity}</span>
                <button type="button" onClick={() => updateQuantity(1)} disabled={quantity >= Number(product.stock || 1)}>+</button>
              </>
            )}
          </div>
        </div>

        <div className="cta-row">
          <button type="button" className="primary-btn" onClick={handleAddToCart}>Add to Cart</button>
          <button type="button" className="secondary-btn" onClick={handleBuyNow}>Buy Now</button>
        </div>

        <button type="button" className="whatsapp-btn" onClick={askOnWhatsApp}>Ask on WhatsApp</button>

        <div className="feature-list">
          <div>
            <strong>✔</strong>
            <span>Handcrafted with premium fabrics</span>
          </div>
          <div>
            <strong>✔</strong>
            <span>Custom sizing available</span>
          </div>
          <div>
            <strong>✔</strong>
            <span>Tailored finish for weddings and events</span>
          </div>
        </div>
      </div>
    </div>
  );
}
